import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  ArrowBack,
  AccountBalanceWalletOutlined,
  Inventory2Outlined,
  AddCardOutlined,
  PieChartOutline,
  TrendingUp,
  ShowChartOutlined,
  ChevronRight,
} from '@mui/icons-material';
import { useSupabase } from '../contexts/SupabaseContext';
import {
  ASSET_CLASSES,
  PRODUCTS,
  assetClassName,
  toHoldings,
  monthBuckets,
  yearBuckets,
  analyseInvestments,
} from '../utils/investmentAnalysis';
import {
  SERIES_COLORS,
  OTHER_COLOR,
  TOP_CATEGORIES,
  periodMonths,
  compactRupees,
  formatRupees,
  formatPercent,
} from '../utils/transactionAnalysis';
import { SummaryTile } from './utilities/FastagManager';
import { Legend, ChartCard, chartSx, money } from '../components/analysis/ChartParts';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';

// Diverging pair for realised profit / loss (validated: CVD ΔE 21.6, both ≥ 3:1 on white)
const GAIN_COLOR = '#2a78d6';
const LOSS_COLOR = '#e34948';
const BAR_COLOR = SERIES_COLORS[0];

const formatDay = (value) => {
  if (!value) return '—';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const InvestmentAnalysis = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [searchParams, setSearchParams] = useSearchParams();

  const [period, setPeriod] = useState(searchParams.get('period') || 'last12');
  const [cls, setCls] = useState(ASSET_CLASSES.some(c => c.key === searchParams.get('class')) ? searchParams.get('class') : 'all');

  const [holdings, setHoldings] = useState([]);
  const [savingsBank, setSavingsBank] = useState(0);
  const [sales, setSales] = useState([]);
  const [failedTables, setFailedTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const tour = useTour('analysis-investments', { ready: !loading });

  // Every holding from every investment table (amount invested), bank balances, stock sales
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const failed = [];
      const results = await Promise.all(PRODUCTS.map(async (product) => {
        const { data, error } = await supabase.from(product.table).select('*').eq('user_id', user.id);
        if (error) {
          console.error(`Investment analysis: ${product.table}`, error.message);
          failed.push(product.label);
          return [];
        }
        return toHoldings(product, data || []);
      }));
      const [{ data: banks }, { data: saleRows, error: salesError }] = await Promise.all([
        supabase.from('wallets').select('balance').eq('user_id', user.id).eq('type', 'bank').not('is_archived', 'is', true),
        supabase.from('stock_sales').select('sale_date, profit_loss, stock_name, net_amount').eq('user_id', user.id),
      ]);
      if (salesError) console.error('Investment analysis: stock_sales', salesError.message);
      if (cancelled) return;
      setHoldings(results.flat());
      setSavingsBank((banks || []).reduce((s, w) => s + (Number(w.balance) || 0), 0));
      setSales(saleRows || []);
      setFailedTables(failed);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [supabase, user]);

  const today = new Date();
  const firstYear = useMemo(() => {
    const years = holdings.flatMap(h => h.flows.map(f => Number(f.date.slice(0, 4)))).filter(Boolean);
    return years.length ? Math.min(...years) : today.getFullYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings]);

  const buckets = useMemo(() => {
    if (period === 'all') return yearBuckets(firstYear);
    return monthBuckets(periodMonths(period).months);
  }, [period, firstYear]);

  const scoped = useMemo(() => (cls === 'all' ? holdings : holdings.filter(h => h.cls === cls)), [holdings, cls]);
  const scopedBank = cls === 'all' || cls === 'cash-and-liquid' ? savingsBank : 0;
  const scopedSales = useMemo(() => (cls === 'all' || cls === 'equity' ? sales : []), [cls, sales]);
  const groupBy = cls === 'all' ? 'cls' : 'product';
  const result = useMemo(
    () => analyseInvestments(scoped, scopedBank, buckets, scopedSales, groupBy),
    [scoped, scopedBank, buckets, scopedSales, groupBy],
  );

  // Colours by all-time size (not the period), so a class/product keeps its colour across periods
  const groupRanking = useMemo(() => {
    const totals = {};
    scoped.forEach((h) => { const k = groupBy === 'cls' ? h.cls : h.product; totals[k] = (totals[k] || 0) + h.flows.reduce((s, f) => s + f.amount, 0); });
    return Object.entries(totals).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [scoped, groupBy]);
  const groupLabel = (key) => (groupBy === 'cls' ? assetClassName(key) : key);
  const topGroups = groupRanking.slice(0, TOP_CATEGORIES);
  const restGroups = groupRanking.slice(TOP_CATEGORIES);
  const stackSeries = [
    ...topGroups.map((g, i) => ({
      id: g, label: groupLabel(g), color: SERIES_COLORS[i], stack: 'invested',
      data: result.rows.map(r => r.perGroup[g] || 0), valueFormatter: money,
    })),
    ...(restGroups.length ? [{
      id: '__other', label: `Other (${restGroups.length})`, color: OTHER_COLOR, stack: 'invested',
      data: result.rows.map(r => restGroups.reduce((s, g) => s + (r.perGroup[g] || 0), 0)), valueFormatter: money,
    }] : []),
  ];

  const setParam = (key, value, fallback) => {
    const next = new URLSearchParams(searchParams);
    if (value === fallback) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const chooseClass = (value) => { setCls(value); setParam('class', value, 'all'); };

  const thisYear = today.getFullYear();
  const yearOptions = [];
  for (let y = thisYear; y >= Math.min(firstYear, thisYear); y -= 1) yearOptions.push(String(y));
  const periodLabel = period === 'all' ? 'all time' : period === 'last12' ? 'the last 12 months' : period === 'last6' ? 'the last 6 months' : period;
  const bucketWord = period === 'all' ? 'year' : 'month';
  const xLabels = result.rows.map(r => r.label);
  const xBand = (gap) => [{ scaleType: 'band', data: xLabels, categoryGapRatio: gap, barGapRatio: 0.15 }];
  const yMoney = [{ valueFormatter: (v) => compactRupees(v) }];

  const holdingsText = (count, withBank) => [count ? `${count} holding${count === 1 ? '' : 's'}` : '', withBank ? 'bank balance' : '']
    .filter(Boolean).join(' + ').replace(/^b/, 'B');
  const ranking = cls === 'all' ? result.classes.map(c => ({ key: c.cls, name: c.name, total: c.total, share: c.share, sub: holdingsText(c.count, c.cls === 'cash-and-liquid' && result.savingsBank > 0) }))
    : result.products.map(p => ({ key: p.product, name: p.product, total: p.total, share: p.share, path: p.path, sub: p.balance ? 'Bank balance today' : `${p.count} holding${p.count === 1 ? '' : 's'}` }));
  const maxRank = ranking[0]?.total || 1;
  const largest = ranking[0];
  const showRealised = (cls === 'all' || cls === 'equity') && sales.length > 0;
  const hasAny = result.total > 0;

  return (
    <Box>
      {/* Sub-header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          What you've invested (at cost), where it sits, and when you put it in
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/investments')}>Investments</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper data-tour="inv-analysis-filters" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select size="small" label="Period" value={period} sx={{ minWidth: 170, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}
          onChange={(e) => { setPeriod(e.target.value); setParam('period', e.target.value, 'last12'); }}
        >
          <MenuItem value="last12">Last 12 months</MenuItem>
          <MenuItem value="last6">Last 6 months</MenuItem>
          {yearOptions.map(y => <MenuItem key={y} value={y}>{y === String(thisYear) ? `This year (${y})` : y}</MenuItem>)}
          <MenuItem value="all">All time (by year)</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Asset class" value={cls} sx={{ minWidth: 190, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}
          onChange={(e) => chooseClass(e.target.value)}
        >
          <MenuItem value="all">All asset classes</MenuItem>
          {ASSET_CLASSES.map(c => <MenuItem key={c.key} value={c.key}>{c.name}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' }, maxWidth: 360 }}>
          Amounts are what you paid. Current values with live prices are on each investment page.
        </Typography>
      </Paper>

      {failedTables.length > 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          Couldn't load: {failedTables.join(', ')}. Those holdings are left out below.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !hasAny ? (
        <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <ShowChartOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
            {cls === 'all' ? 'No investments yet' : `No ${assetClassName(cls)} investments`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Add investments to see where your money is and how it has grown.</Typography>
          <Button variant="contained" onClick={() => navigate(cls === 'all' ? '/investments' : ASSET_CLASSES.find(c => c.key === cls).path)}>
            Go to Investments
          </Button>
        </Card>
      ) : (
        <>
          {/* Headline numbers */}
          <Paper data-tour="inv-analysis-summary" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${showRealised ? 5 : 4}, 1fr)` }, gap: { xs: 1, sm: 1.5 } }}>
            <SummaryTile icon={<AccountBalanceWalletOutlined />} color="primary" value={formatRupees(result.total)} label={cls === 'all' ? 'Total invested' : `${assetClassName(cls)} invested`} />
            <SummaryTile icon={<Inventory2Outlined />} color="info" value={result.holdingsCount} label={result.holdingsCount === 1 ? 'Holding' : 'Holdings'} />
            <SummaryTile icon={<AddCardOutlined />} color="success" value={formatRupees(result.investedInPeriod)} label={`Put in, ${periodLabel}`} />
            <SummaryTile
              icon={<PieChartOutline />} color="secondary"
              value={largest ? formatPercent(largest.share) : '—'}
              label={largest ? `Largest: ${largest.name}` : 'Largest'}
            />
            {showRealised && (
              <SummaryTile
                icon={<TrendingUp />} color={result.realisedInPeriod >= 0 ? 'success' : 'error'}
                value={formatRupees(result.realisedInPeriod)}
                label={`Realised P/L on stocks (${result.salesInPeriod} sale${result.salesInPeriod === 1 ? '' : 's'})`}
              />
            )}
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' }, gap: 3, mb: 3, alignItems: 'start' }}>
            {/* Allocation: ranked bars (classes, or products inside the chosen class) */}
            <ChartCard
              data-tour="inv-analysis-allocation"
              title={cls === 'all' ? 'Where Your Money Is' : `${assetClassName(cls)}: By Product`}
              subtitle={cls === 'all' ? 'Share of the total by asset class. Click one to see its products.' : 'Click a product to open it.'}
              style={{ marginBottom: 0 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }} role="group" aria-label="Allocation">
                {ranking.map((item) => (
                  <Box
                    key={item.key}
                    component="button"
                    type="button"
                    onClick={() => (cls === 'all' ? chooseClass(item.key) : navigate(item.path))}
                    aria-label={`${item.name}: ${formatRupees(item.total)}, ${formatPercent(item.share)}`}
                    sx={{
                      all: 'unset', boxSizing: 'border-box', cursor: 'pointer', width: '100%', px: 1, py: 0.75, borderRadius: 1.5,
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div" noWrap>{item.sub}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatRupees(item.total)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatPercent(item.share)}</Typography>
                      <ChevronRight sx={{ fontSize: 18, color: 'text.secondary', alignSelf: 'center' }} />
                    </Box>
                    <Box sx={{ height: 8, borderRadius: '0 4px 4px 0', bgcolor: BAR_COLOR, width: `${Math.max(1, (item.total / maxRank) * 100)}%` }} />
                  </Box>
                ))}
              </Box>
              {cls !== 'all' && (
                <Button size="small" sx={{ mt: 1.5 }} startIcon={<ArrowBack />} onClick={() => chooseClass('all')}>All asset classes</Button>
              )}
            </ChartCard>

            {/* Running total */}
            <ChartCard
              data-tour="inv-analysis-growth"
              title="Total Invested Over Time"
              subtitle={`Running total at the end of each ${bucketWord}, from purchase dates${result.savingsBank ? '. Bank balances have no purchase date, so they are not in this line' : ''}.`}
              style={{ marginBottom: 0 }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <LineChart
                  height={300}
                  xAxis={[{ scaleType: 'point', data: xLabels }]}
                  yAxis={yMoney}
                  series={[{ id: 'cumulative', label: 'Total invested', data: result.rows.map(r => r.cumulative), color: BAR_COLOR, area: true, showMark: result.rows.length <= 12, valueFormatter: money }]}
                  grid={{ horizontal: true }}
                  margin={{ top: 12, right: 16, bottom: 28, left: 60 }}
                  slotProps={{ legend: { hidden: true } }}
                  sx={{
                    ...chartSx,
                    '& .MuiLineElement-root': { strokeWidth: 2 },
                    '& .MuiAreaElement-root': { fill: alpha(BAR_COLOR, 0.12) },
                  }}
                />
              </Box>
            </ChartCard>
          </Box>

          {/* Money put in per bucket */}
          <ChartCard
            data-tour="inv-analysis-monthly"
            title={`Invested ${period === 'all' ? 'Year' : 'Month'} by ${period === 'all' ? 'Year' : 'Month'}`}
            subtitle={`New money put in each ${bucketWord}, split by ${groupBy === 'cls' ? 'asset class' : 'product'} (by purchase date; RDs by instalment). Hover a bar for the amounts.`}
          >
            <Legend items={stackSeries.map(s => ({ label: s.label, color: s.color }))} />
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <BarChart
                height={300}
                xAxis={xBand(0.6)}
                yAxis={yMoney}
                series={stackSeries}
                grid={{ horizontal: true }}
                borderRadius={4}
                margin={{ top: 12, right: 8, bottom: 28, left: 60 }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>
          </ChartCard>

          {/* Realised P/L from stock sales */}
          {showRealised && (
            <ChartCard
              data-tour="inv-analysis-realised"
              title="Realised Profit / Loss on Stocks"
              subtitle={`From your stock sales (FIFO cost), by sale ${bucketWord}. ${result.salesInPeriod ? `${formatRupees(result.realisedInPeriod)} in ${periodLabel}.` : `No sales in ${periodLabel}.`}`}
            >
              <Legend items={[{ label: 'Profit', color: GAIN_COLOR }, { label: 'Loss', color: LOSS_COLOR }]} />
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <BarChart
                  height={240}
                  xAxis={xBand(0.6)}
                  yAxis={yMoney}
                  series={[
                    { id: 'gain', label: 'Profit', stack: 'pl', color: GAIN_COLOR, data: result.rows.map(r => Math.max(0, r.realised)), valueFormatter: (v) => (v ? formatRupees(v) : null) },
                    { id: 'loss', label: 'Loss', stack: 'pl', color: LOSS_COLOR, data: result.rows.map(r => Math.min(0, r.realised)), valueFormatter: (v) => (v ? formatRupees(v) : null) },
                  ]}
                  grid={{ horizontal: true }}
                  borderRadius={4}
                  margin={{ top: 12, right: 8, bottom: 28, left: 60 }}
                  slotProps={{ legend: { hidden: true } }}
                  sx={chartSx}
                />
              </Box>
            </ChartCard>
          )}

          {/* Table */}
          <ChartCard data-tour="inv-analysis-table" title="Holdings by Product" subtitle="Every product you hold, with what you've invested and its share of the total.">
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small" aria-label="Investments by product">
                <TableHead>
                  <TableRow>
                    <TableCell>Asset class</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Holdings</TableCell>
                    <TableCell align="right">Invested</TableCell>
                    <TableCell align="right">Share</TableCell>
                    <TableCell>First purchase</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.products.map(p => (
                    <TableRow key={`${p.cls}|${p.product}`} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(p.path)}>
                      <TableCell>{assetClassName(p.cls)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{p.product}</TableCell>
                      <TableCell align="right">{p.balance ? '—' : p.count}</TableCell>
                      <TableCell align="right">{formatRupees(p.total)}</TableCell>
                      <TableCell align="right">{formatPercent(p.share)}</TableCell>
                      <TableCell>{p.balance ? 'Balance today' : formatDay(p.first)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderTopColor: 'divider' } }}>
                    <TableCell colSpan={2}>Total</TableCell>
                    <TableCell align="right">{result.holdingsCount}</TableCell>
                    <TableCell align="right">{formatRupees(result.total)}</TableCell>
                    <TableCell align="right">100%</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {result.undated > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {formatRupees(result.undated)} has no purchase date, so it is in the totals but not in the charts over time.
              </Typography>
            )}
          </ChartCard>
        </>
      )}

      <GuidedTour steps={TOURS['analysis-investments']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default InvestmentAnalysis;
